import {
  ExtensionTag,
  NodeExtension,
  isElementDomNode,
  extension,
  command,
  CommandFunction,
  htmlToProsemirrorNode,
  findParentNode,
} from 'remirror'
import { TextSelection } from '@remirror/pm/state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import type {
  CreateExtensionPlugin,
  NodeExtensionSpec,
  NodeViewMethod,
} from 'remirror'

import {
  BLOCK_DATA_ATTRIBUTE,
  BLOCK_TYPE_DATA_ATTRIBUTE,
  EXTENSION_NAME,
  getBlockHTML,
} from './utils'

@extension({
  disableExtraAttributes: true,
  defaultOptions: {},
})
class KeyValueBlockExtension extends NodeExtension {
  get name() {
    return EXTENSION_NAME
  }

  createTags() {
    return [ExtensionTag.Block]
  }

  createNodeViews(): NodeViewMethod {
    return (node, view, getPosition) => {
      // attribute states
      const rootNode: boolean = node.attrs.isRoot
      const nodeType: 'header' | 'content' | null = node.attrs.type
      const isHeader = node.attrs.type === 'header'

      // other states
      const isEditable = view.editable
      const contentEditable = isEditable && !isHeader
      const showEditButton = isEditable && isHeader && !contentEditable

      // container div
      const containerDiv = document.createElement('div')
      containerDiv.className = 'key-value-block-container'
      if (rootNode) {
        containerDiv.setAttribute(BLOCK_DATA_ATTRIBUTE, 'true')
      }

      // content div
      const contentDiv = document.createElement('span')
      if (nodeType) {
        contentDiv.setAttribute(BLOCK_TYPE_DATA_ATTRIBUTE, nodeType)
      }
      if (isHeader) {
        contentDiv.contentEditable = contentEditable.toString()

        // change contentEditable to false of header block if focus is not on it
        const observer = new MutationObserver(mutationList => {
          mutationList.forEach(mutation => {
            const _node = mutation.target as HTMLDivElement
            const isBlockSelected = _node.classList.contains('selected')

            const headerElement = _node.querySelector<HTMLSpanElement>(
              `[${BLOCK_TYPE_DATA_ATTRIBUTE}=header]`
            )
            if (!headerElement) return

            if (!isBlockSelected) {
              headerElement.contentEditable = 'false'
            }
          })
        })
        observer.observe(containerDiv, { attributes: true })
      }

      // action container
      const actionContainer = document.createElement('div')
      actionContainer.contentEditable = 'false'
      actionContainer.className = 'key-value-block-action-container'

      // action buttons
      const editButton = document.createElement('button')
      editButton.innerText = 'Edit'
      editButton.onclick = () => {
        contentDiv.contentEditable = 'true'
        // * * focus at the end of the key-value block
        const position = (getPosition as () => number)()
        if (!position) return
        const selection = TextSelection.near(
          view.state.doc.resolve(position + node.content.size)
        )
        view.dispatch(view.state.tr.setSelection(selection))
        view.focus()
      }

      // appending elements
      containerDiv.append(contentDiv)
      if (showEditButton) {
        actionContainer.append(editButton)
        containerDiv.append(actionContainer)
      }

      return { dom: containerDiv, contentDOM: contentDiv }
    }
  }

  createNodeSpec(): NodeExtensionSpec {
    return {
      attrs: {
        key: { default: null },
        type: { default: null },
        isRoot: { default: false },
      },
      content: 'block*',
      group: 'key_value_block',
      defining: false,
      selectable: false,
      isolating: true,
      parseDOM: [
        {
          tag: `[${BLOCK_DATA_ATTRIBUTE}]`,
          attrs: {},
          getAttrs: node => {
            if (!isElementDomNode(node)) {
              return false
            }

            const key =
              node.attributes.getNamedItem(BLOCK_DATA_ATTRIBUTE)?.value
            return { key, isRoot: true }
          },
        },
        {
          tag: `[${BLOCK_TYPE_DATA_ATTRIBUTE}]`,
          attrs: {},
          getAttrs: node => {
            if (!isElementDomNode(node)) {
              return false
            }

            const key =
              node.attributes.getNamedItem(BLOCK_DATA_ATTRIBUTE)?.value
            const type = node.attributes.getNamedItem(
              BLOCK_TYPE_DATA_ATTRIBUTE
            )?.value

            return { key, type, isRoot: false }
          },
        },
      ],
      toDOM: node => {
        const rootAttr = {
          [BLOCK_DATA_ATTRIBUTE]: node.attrs.key,
        }
        const itemAttr = {
          [BLOCK_TYPE_DATA_ATTRIBUTE]: node.attrs.type,
        }

        if (node.attrs.isRoot) return ['div', rootAttr, 0]
        else return ['span', itemAttr, 0]
      },
    }
  }

  createPlugin(): CreateExtensionPlugin {
    return {
      props: {
        decorations(state) {
          // adding selected class to the first parent element with type attribute = [header, content]
          const parentNode = findParentNode({
            predicate: node => ['header', 'content'].includes(node.attrs.type),
            selection: state.selection,
          })
          if (!parentNode) return

          const parentResolvedPosition = state.doc.resolve(parentNode.start)
          const decoration = Decoration.node(
            parentResolvedPosition.before(),
            parentResolvedPosition.after(),
            { class: 'selected' }
          )
          return DecorationSet.create(state.doc, [decoration])
        },
      },
    }
  }

  @command()
  insertKeyValueBlock(): CommandFunction {
    return ({ tr, dispatch, state }) => {
      if (!dispatch) return false

      const node = htmlToProsemirrorNode({
        content: getBlockHTML(),
        schema: state.schema,
      })
      dispatch(tr.insert(state.selection.from, node))
      return true
    }
  }
}

export default KeyValueBlockExtension
